// Enerģijas baterijas modelis (vienībās, 0…ietilpība). Ietilpību nosaka pamata ietilpība un jomu līmeņi;
// galvenie avoti — miegs (naktī) un ēdiens (ēdienreizēs), patēriņš — nomoda pamatpatēriņš, fona jomas un aktivitātes (kārtis).
// Laiks tiek rēķināts lietotāja laika joslā (IANA), lai serveris (UTC) un pārlūks sakristu. Sk. ADR 0012.

import { CAP, EMAX, KINDS } from './constants';
import { clamp } from './format';
import type { Index } from './tree';
import type { CapacityTier, EnergySettings, JournalEntry, Kind, Role, ShieldKind } from './types';

export const DAY = 86_400_000;

export const toMin = (t: string | undefined | null): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t || '');
  return m && +m[1] < 24 && +m[2] < 60 ? +m[1] * 60 + +m[2] : null;
};

export interface Times {
  w: number; // celšanās minūte
  b: number; // gulētiešanas minūte
  awake: number; // nomoda minūtes diennaktī
  sleep: number; // miega minūtes diennaktī
}

export function eTimes(E: Pick<EnergySettings, 'wake' | 'bed'>): Times {
  const w0 = toMin(E.wake);
  const b0 = toMin(E.bed);
  const w = w0 == null ? 420 : w0;
  const b = b0 == null ? 1380 : b0;
  const awake = (b - w + 1440) % 1440 || 1440;
  return { w, b, awake, sleep: 1440 - awake };
}

export const awakeMin = (m: number, T: Times) => !T.sleep || (T.w < T.b ? m >= T.w && m < T.b : m >= T.w || m < T.b);

/** Uzlādes kvalitāte: vērtība 0 → 30 %, 5 → 65 %, 10 → 100 %. */
export const chargeF = (v: number) => 0.3 + (0.7 * v) / 10;
/** Patēriņa reizinātājs: vērtība 0 → ×1,5, 5 → ×1, 10 → ×0,5 (nesakārtota joma rada stresu). */
export const drainF = (v: number) => 1.5 - v / 10;

/** Aktīvais ietilpības līmenis: ar lielāko `min`, kas nepārsniedz vērtību; zem visiem — null. */
export function tierAt(tiers: CapacityTier[] | null | undefined, v: number): CapacityTier | null {
  let best: CapacityTier | null = null;
  for (const t of tiers || []) if (v + 1e-9 >= t.min && (!best || t.min > best.min)) best = t;
  return best;
}

/** Pamata ietilpība no iestatījumiem (robežās). */
export const capBaseOf = (E: Pick<EnergySettings, 'capacity'>) => {
  const c = Number(E.capacity);
  return Number.isFinite(c) ? clamp(Math.round(c), CAP.baseMin, CAP.baseMax) : 100;
};

/** Derīgie ēdienreižu laiki minūtēs (sakārtoti, bez dublikātiem). */
export const mealMinutes = (E: Pick<EnergySettings, 'meals'>): number[] =>
  [...new Set((Array.isArray(E.meals) ? E.meals : []).map(toMin).filter((m): m is number => m != null))].sort((a, b) => a - b);

export interface EnergyRow {
  id: string;
  role: Role;
  kind?: ShieldKind;
  eff: number; // + uzlāde, − patēriņš (vienības diennaktī); noturībai — samazinājums %
}

/** Jomas ieguldījums ietilpībā: `min` — aktīvā līmeņa robeža (null — zem visiem līmeņiem). */
export interface CapRow {
  id: string;
  units: number;
  min: number | null;
}

export interface EnergyModel {
  rows: EnergyRow[];
  /** Ietilpība (vienības) = pamata + jomu līmeņi, robežās. */
  cap: number;
  capBase: number;
  capRows: CapRow[];
  /** Nakts uzlāde (miegs), vienības diennaktī. */
  sleep: number;
  /** Ēdiena uzlāde, vienības diennaktī. */
  food: number;
  /** Ēdienreižu minūtes; uzlāde vienā ēdienreizē. Ja ēdienreižu nav — ēdiens ir daļa no `dayDelta`. */
  meals: number[];
  mealPortion: number;
  /** Vienmērīga uzlāde dienā (loma `charge`). */
  day: number;
  /** Patēriņš: fona jomas + pamatpatēriņš (pēc noturības). */
  drain: number;
  base: number;
  /** Tipiskās aktivitātes (kārtis) diennaktī — tikai prognozei. */
  act: number;
  remain: Record<Kind, number>;
  sleepDur: number;
  T: Times;
  /** Vienmērīgā izmaiņa nomodā (bez ēdienreizēm un aktivitātēm). */
  dayDelta: number;
  /** Visa nomoda perioda izmaiņa bez aktivitātēm (ar ēdienu). */
  dayNet: number;
  /** Diennakts bilance ar tipiskajām aktivitātēm. */
  net: number;
}

export function energyModel(idx: Index, E: EnergySettings, valOf?: (id: string) => number, act = 0): EnergyModel {
  const vo = valOf || ((id: string) => idx.val.get(id) ?? 0);
  const T = eTimes(E);
  const rows: EnergyRow[] = [];
  const capRows: CapRow[] = [];
  const remain: Record<Kind, number> = { fiz: 1, gar: 1, emo: 1, soc: 1 };

  const capBase = capBaseOf(E);
  let capSum = capBase;
  idx.node.forEach((n) => {
    if (!n.tiers || !n.tiers.length) return;
    const t = tierAt(n.tiers, clamp(vo(n.id), 0, 10));
    const units = t ? t.units : 0;
    capSum += units;
    capRows.push({ id: n.id, units, min: t ? t.min : null });
  });
  const cap = clamp(capSum, CAP.min, CAP.max);

  idx.node.forEach((n) => {
    const e = n.energy;
    if (!e || e.role !== 'shield') return;
    const r = (clamp(e.amount, 0, EMAX.shield) / 100) * (clamp(vo(n.id), 0, 10) / 10);
    (e.kind === 'all' ? KINDS : [e.kind as Kind]).forEach((k) => {
      if (remain[k] !== undefined) remain[k] *= 1 - r;
    });
    rows.push({ id: n.id, role: 'shield', kind: e.kind, eff: r * 100 });
  });
  KINDS.forEach((k) => {
    remain[k] = Math.max(remain[k], 0.2);
  });
  const sleepDur = clamp(T.sleep / 480, 0, 1);
  let sleep = 0;
  let food = 0;
  let day = 0;
  let drain = 0;
  idx.node.forEach((n) => {
    const e = n.energy;
    if (!e || e.role === 'shield') return;
    const v = clamp(vo(n.id), 0, 10);
    if (e.role === 'sleep') {
      const x = cap * (clamp(e.amount, 0, EMAX.sleep) / 100) * chargeF(v) * sleepDur;
      sleep += x;
      rows.push({ id: n.id, role: 'sleep', eff: x });
    } else if (e.role === 'food') {
      const x = cap * (clamp(e.amount, 0, EMAX.food) / 100) * chargeF(v);
      food += x;
      rows.push({ id: n.id, role: 'food', eff: x });
    } else if (e.role === 'charge') {
      const x = e.amount * chargeF(v);
      day += x;
      rows.push({ id: n.id, role: 'charge', eff: x });
    } else if (e.role === 'drain') {
      const k: Kind = (KINDS as string[]).includes(e.kind) ? (e.kind as Kind) : 'fiz';
      const x = e.amount * drainF(v) * remain[k];
      drain += x;
      rows.push({ id: n.id, role: 'drain', kind: k, eff: -x });
    }
  });
  const base = clamp(Number(E.base) || 0, 0, 60) * (remain[E.baseKind] ?? 1);
  drain += base;
  const meals = food > 0 ? mealMinutes(E) : [];
  const mealPortion = meals.length ? food / meals.length : 0;
  const dayDelta = day - drain + (meals.length ? 0 : food);
  const dayNet = day + food - drain;
  return {
    rows, cap, capBase, capRows, sleep, food, meals, mealPortion, day, drain, base, act, remain, sleepDur, T,
    dayDelta, dayNet, net: sleep + dayNet + act
  };
}

/**
 * Tipiskās aktivitātes: kāršu enerģijas summa pēdējās `days` dienās, dalīta ar novēroto dienu skaitu
 * (jaunam lietotājam — ar dienām kopš pirmās izspēles, vismaz 1).
 */
export function typicalActivity(journal: Pick<JournalEntry, 'ts' | 'energyDelta'>[], now: number, days = 7): number {
  const past = journal.filter((j) => j.ts <= now);
  const list = past.filter((j) => j.ts > now - days * DAY);
  if (!list.length) return 0;
  const first = Math.min(...past.map((j) => j.ts));
  const span = clamp(Math.ceil((now - first) / DAY), 1, days);
  return list.reduce((s, j) => s + (Number(j.energyDelta) || 0), 0) / span;
}

// ---------- laika joslas ----------

const dtfCache = new Map<string, Intl.DateTimeFormat>();
/** Laika joslas nobīde minūtēs (vietējais − UTC), piem. Rīgā vasarā +180. */
export function tzOffsetMin(ts: number, tz?: string | null): number {
  if (!tz) return -new Date(ts).getTimezoneOffset();
  let dtf = dtfCache.get(tz);
  if (!dtf) {
    try {
      dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch {
      return -new Date(ts).getTimezoneOffset();
    }
    dtfCache.set(tz, dtf);
  }
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(new Date(ts))) p[part.type] = part.value;
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUTC - Math.floor(ts / 1000) * 1000) / 60000);
}

export const localMinute = (ts: number, off: number) => (((Math.floor(ts / 60000) + off) % 1440) + 1440) % 1440;

/** Laika zīmogs nākamajā (after=true) vai iepriekšējā reizē, kad pulkstenis ir `m` minūtes. */
export function atClock(now: number, m: number, after: boolean, tz?: string | null): number {
  const off = tzOffsetMin(now, tz);
  const localMin = Math.floor(now / 60000) + off;
  const dayStart = Math.floor(localMin / 1440) * 1440;
  let ts = (dayStart + m - off) * 60000;
  if (after ? ts <= now : ts > now) ts += after ? DAY : -DAY;
  return ts;
}

/**
 * Simulācija pa 10 minūšu soļiem: nomodā vienmērīgā izmaiņa (+ tipiskās aktivitātes, ja `typical`), ēdienreizēs — porcija,
 * miegā — nakts uzlāde. Robežas 0…ietilpība.
 */
export function simulate(
  M: EnergyModel,
  from: number,
  level: number,
  to: number,
  opts: { every?: number; cb?: (t: number, L: number, awake: boolean) => void; tz?: string | null; typical?: boolean } = {}
): number {
  const T = M.T;
  const step = 600_000;
  const off = tzOffsetMin(from, opts.tz);
  const rA = (M.dayDelta + (opts.typical ? M.act : 0)) / T.awake;
  const rS = T.sleep ? M.sleep / T.sleep : 0;
  const aw = (t: number) => awakeMin(localMinute(t, off), T);
  const meals = M.mealPortion ? M.meals.map((m) => m * 60000) : [];
  const offMs = off * 60000;
  const every = opts.every || 1;
  let t = from;
  let L = clamp(level, 0, M.cap);
  let k = 0;
  if (opts.cb) opts.cb(t, L, aw(t));
  while (t < to) {
    const dt = Math.min(step, to - t);
    const a = aw(t);
    L += ((a ? rA : rS) * dt) / 60000;
    if (meals.length) {
      // ēdienreize intervālā (t, t + dt] — veselos ms, lai robeža netiktu ieskaitīta divreiz
      const lt = (((t + offMs) % DAY) + DAY) % DAY;
      for (const m of meals) {
        const d = (((m - lt) % DAY) + DAY) % DAY;
        if (d > 0 && d <= dt) L += M.mealPortion;
      }
    }
    L = clamp(L, 0, M.cap);
    t += dt;
    k++;
    if (opts.cb && (k % every === 0 || t >= to)) opts.cb(t, L, aw(t));
  }
  return L;
}

/** Ilgtermiņa līmenis rītos un vakaros, ja katra diena ir “tipiska”. */
export function steadyLevels(M: EnergyModel) {
  const dayAll = M.dayNet + M.act;
  let m = M.cap;
  for (let i = 0; i < 90; i++) m = clamp(clamp(m + dayAll, 0, M.cap) + M.sleep, 0, M.cap);
  return { morning: m, evening: clamp(m + dayAll, 0, M.cap) };
}

/** Noklusējuma enkurs: pēdējā celšanās ar pilnu bateriju (`level` — ietilpība). */
export const defaultAnchor = (now: number, E: Pick<EnergySettings, 'wake' | 'bed'>, tz?: string | null, level = 100) => ({
  ts: atClock(now, eTimes(E).w, false, tz),
  level
});

export interface EnergyNow {
  M: EnergyModel;
  now: number;
  /** Līmenis vienībās. */
  L: number;
  /** Līmenis % no ietilpības. */
  pct: number;
  awake: boolean;
  nextEnd: number;
  atEnd: number;
  zeroAt: number | null;
  up: Map<string, number>;
  st: { morning: number; evening: number };
}

/** Līmenis % no ietilpības. */
export const levelPct = (L: number, cap: number) => (cap > 0 ? clamp((L / cap) * 100, 0, 100) : 0);

/**
 * Enerģijas līmenis tagad (simulēts no enkura) un tā konteksts. `act` — tipiskās aktivitātes diennaktī (prognozei;
 * sk. `typicalActivity`). `full=false` izlaiž dārgākos aprēķinus (prognozi uz nulli un uzlabojuma ietekmi) — serverim pietiek ar L.
 */
export function computeEnergy(idx: Index, E: EnergySettings, now: number, tz?: string | null, full = true, act = 0): EnergyNow {
  const M = energyModel(idx, E, undefined, act);
  const a = E.anchor || defaultAnchor(now, E, tz, M.cap);
  let from = Math.min(a.ts, now);
  let L0 = a.level;
  const st = steadyLevels(M);
  if (now - from > 120 * DAY) {
    from = now - 120 * DAY;
    L0 = st.morning;
  }
  const L = simulate(M, from, L0, now, { tz });
  const awake = awakeMin(localMinute(now, tzOffsetMin(now, tz)), M.T);
  const nextEnd = atClock(now, awake ? M.T.b : M.T.w, true, tz);
  const atEnd = simulate(M, now, L, nextEnd, { tz, typical: true });
  let zeroAt: number | null = null;
  const up = new Map<string, number>();
  if (full) {
    simulate(M, now, L, now + 14 * DAY, {
      tz,
      typical: true,
      cb: (t, x) => {
        if (zeroAt == null && x <= 0.5) zeroAt = t;
      }
    });
    idx.tops.forEach((t) => {
      const m2 = energyModel(idx, E, (id) => ((idx.anc.get(id) || []).includes(t) ? 10 : idx.val.get(id) ?? 0), act);
      up.set(t, m2.net - M.net);
    });
  }
  return { M, now, L, pct: levelPct(L, M.cap), awake, nextEnd, atEnd, zeroAt, up, st };
}

/** Pašreizējais līmenis kā jauns enkurs — pirms izmaiņām, kas maina modeli (lai tās nedarbotos retroaktīvi). */
export function reanchor(idx: Index, E: EnergySettings, now: number, tz?: string | null): EnergySettings {
  const en = computeEnergy(idx, E, now, tz, false);
  return { ...E, anchor: { ts: now, level: Math.round(en.L * 100) / 100 } };
}
